import { useState } from "react";
import { FiShield, FiClock } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel";
import { InvitationBanner } from "../steps/InitialEvaluationStep/panel";
import { DeclineDrawer } from "../steps/InitialEvaluationStep/panel";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type {
	CurrentUserContext,
	EvaluationAccess,
	EvaluationState,
} from "../../types/evaluation.type";

interface RfxLeadInfo {
	email: string;
	fullName: string;
	phoneNumber?: string;
}

interface EvaluationAccessBoundaryProps {
	children: React.ReactNode;
	access?: EvaluationAccess;
	error?: unknown;
	rfxData: Rfx;
	evaluationState: EvaluationState | null;
	currentUserContext: CurrentUserContext | null;
	onAccept?: () => void;
	onDecline?: () => void;
}

const isAxios403 = (error: unknown): boolean => {
	if (!error || typeof error !== "object") {
		return false;
	}
	const err = error as { response?: { status?: number }; status?: number };
	return err.response?.status === 403 || err.status === 403;
};

const handleGoBack = (): void => {
	window.history.back();
};

interface DeniedShellProps {
	icon: React.ReactNode;
	iconWrapClass: string;
	title: string;
	message: string;
	testId: string;
	rfxLead?: RfxLeadInfo;
	children?: React.ReactNode;
}

const DeniedShell: React.FC<DeniedShellProps> = ({
	icon,
	iconWrapClass,
	title,
	message,
	testId,
	rfxLead,
	children,
}) => (
	<div
		data-testid={testId}
		className="flex min-h-[60vh] items-center justify-center p-6"
	>
		<div className="flex w-full max-w-md flex-col items-center rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
			<div
				className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${iconWrapClass}`}
			>
				{icon}
			</div>
			<h2 className="text-xl font-semibold text-gray-900">{title}</h2>
			<p className="mt-2 text-sm text-gray-500">{message}</p>
			{children}
			{rfxLead && (
				<div className="mt-4 w-full">
					<RfxLeadInfoBox rfxLead={rfxLead} />
				</div>
			)}
			<Button
				btntype="secondary"
				onClick={handleGoBack}
				data-testid={`${testId}-back`}
				className="mt-6"
			>
				Go Back
			</Button>
		</div>
	</div>
);

const RevokedScreen: React.FC<{ rfxLead?: RfxLeadInfo }> = ({ rfxLead }) => (
	<DeniedShell
		testId="evaluation-access-revoked"
		iconWrapClass="bg-amber-100"
		icon={
			<FiShield
				className="h-6 w-6 text-amber-600"
				data-testid="evaluation-access-revoked-icon"
				aria-hidden="true"
			/>
		}
		title="Access Denied"
		message="Your access to this evaluation is denied. If you believe this is an error, please contact the RFx Lead."
		rfxLead={rfxLead}
	/>
);

interface PendingConflictScreenProps {
	rfxData: Rfx;
	evaluationState: EvaluationState | null;
	currentUserContext: CurrentUserContext | null;
	onDecline?: () => void;
}

const PendingConflictScreen: React.FC<PendingConflictScreenProps> = ({
	rfxData,
	evaluationState,
	currentUserContext,
	onDecline,
}) => {
	const [showDeclineDrawer, setShowDeclineDrawer] = useState(false);

	return (
		<DeniedShell
			testId="evaluation-access-pending-conflict"
			iconWrapClass="bg-blue-100"
			icon={
				<FiClock
					className="h-6 w-6 text-blue-600"
					data-testid="evaluation-access-pending-conflict-icon"
					aria-hidden="true"
				/>
			}
			title="Access Pending Review"
			message="You declared a conflict of interest when accepting this invitation. Your access is on hold while the RFx Lead reviews the declared conflict. You will be notified once a decision has been made."
			rfxLead={rfxData?.rfxLead}
		>
			<button
				type="button"
				onClick={() => setShowDeclineDrawer(true)}
				className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-800"
				data-testid="evaluation-access-pending-conflict-withdraw"
			>
				I can no longer participate
			</button>
			<DeclineDrawer
				isOpen={showDeclineDrawer}
				onClose={() => setShowDeclineDrawer(false)}
				rfxId={rfxData?.id}
				evalId={evaluationState?.id ?? 0}
				memberId={currentUserContext?.panelMemberId ?? 0}
				rfxLeadName={rfxData?.rfxLead?.fullName}
				rfxLeadEmail={rfxData?.rfxLead?.email}
				rfxTitle={rfxData?.name}
				onDeclined={onDecline}
			/>
		</DeniedShell>
	);
};

interface InvitedScreenProps {
	rfxData: Rfx;
	evaluationState: EvaluationState | null;
	currentUserContext: CurrentUserContext | null;
	onAccept?: () => void;
	onDecline?: () => void;
}

const InvitedScreen: React.FC<InvitedScreenProps> = ({
	rfxData,
	evaluationState,
	currentUserContext,
	onAccept,
	onDecline,
}) => (
	<DeniedShell
		testId="evaluation-access-invited"
		iconWrapClass="bg-amber-100"
		icon={
			<FiShield
				className="h-6 w-6 text-amber-600"
				data-testid="evaluation-access-invited-icon"
				aria-hidden="true"
			/>
		}
		title="Access Denied"
		message="You need to accept your invitation before you can view this evaluation."
	>
		<div className="mt-4 w-full text-left">
			<InvitationBanner
				evalId={evaluationState?.id ?? 0}
				memberId={currentUserContext?.panelMemberId ?? 0}
				rfxData={rfxData}
				rfxTitle={rfxData?.name}
				currentStatus={currentUserContext?.panelMemberStatus ?? "invited"}
				currentStep={evaluationState?.currentStep ?? 0}
				disclaimerRequired={currentUserContext?.disclaimerRequired}
				onAccept={onAccept}
				onDecline={onDecline}
			/>
		</div>
	</DeniedShell>
);

export const EvaluationAccessBoundary: React.FC<
	EvaluationAccessBoundaryProps
> = ({
	access,
	error,
	rfxData,
	evaluationState,
	currentUserContext,
	onAccept,
	onDecline,
	children,
}) => {
	const rfxLead = rfxData?.rfxLead;

	// Defensive fallback: an explicit 403 is always treated as denied.
	if (isAxios403(error)) {
		return <RevokedScreen rfxLead={rfxLead} />;
	}

	if (access === "revoked" || access === "not_panel_member") {
		return <RevokedScreen rfxLead={rfxLead} />;
	}

	if (access === "pending_conflict_review") {
		return (
			<PendingConflictScreen
				rfxData={rfxData}
				evaluationState={evaluationState}
				currentUserContext={currentUserContext}
				onDecline={onDecline}
			/>
		);
	}

	if (access === "invited" || access === "declined") {
		return (
			<InvitedScreen
				rfxData={rfxData}
				evaluationState={evaluationState}
				currentUserContext={currentUserContext}
				onAccept={onAccept}
				onDecline={onDecline}
			/>
		);
	}

	return <>{children}</>;
};
